import { TeambinderApiService } from '@src/api';
import { useQuery } from '@tanstack/react-query';
import { Button, Progress, Modal } from 'antd';
import dayjs from 'dayjs';
import { CSSProperties, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FreeTrialModal from '@src/components/shared/FreeTrialModal';

interface Prop {
  containerStyle?: CSSProperties;
  showTalkQuota?: boolean;
}

const SettingQuotaPane = (prop: Prop) => {
  const { containerStyle, showTalkQuota = true } = prop;
  const navigate = useNavigate();
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);

  const { data: userResponse, refetch: refetchSetting } = useQuery(
    ['member'],
    () => TeambinderApiService.getPersonalSettings({}),
  );
  const { data: currentSubscription } = useQuery(
    ['current-subscription'],
    () => TeambinderApiService.getLatestSubscription({ q: 'latest' }),
    { refetchInterval: 3000 },
  );

  // TODO: openapi 스펙 이상함
  const currentPlan =
    // @ts-ignore
    currentSubscription?.data?.plan?.subscriptionPackage?.subscriptionPlan;

  // Trial 만료 체크 로직
  useEffect(() => {
    if (
      !userResponse?.data?.trial?.endsAt ||
      userResponse?.data?.trial?.status !== 'ACTIVE'
    ) {
      return;
    }

    const today = dayjs();
    const endDate = dayjs(userResponse.data.trial.endsAt);
    const daysLeft = endDate.diff(today, 'day');

    if (daysLeft === 1 || daysLeft === 2 || daysLeft === 3) {
      const storageKey = `trial-expiration-shown-${daysLeft}`;
      const hasShown = localStorage.getItem(storageKey);

      if (!hasShown) {
        setDaysRemaining(daysLeft);
        setShowExpirationModal(true);
        localStorage.setItem(storageKey, 'true');
      }
    }
  }, [userResponse]);

  return (
    <div style={{ ...containerStyle }}>
      {showTalkQuota && (
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              width: '100%',
              maxWidth: '460px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span>현재 알림톡 발송 숫자</span>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {userResponse?.data?.quota?.notification?.total === 999 ? (
                  <span>무제한</span>
                ) : (
                  <>
                    {Math.min(
                      userResponse?.data?.quota?.notification?.used || 0,
                      userResponse?.data?.quota?.notification?.total || 0,
                    )}
                    /{userResponse?.data?.quota?.notification?.total}개
                    {currentPlan !== 'PREMIUM' &&
                      (userResponse?.data?.quota?.notification?.used || 0) >=
                        (userResponse?.data?.quota?.notification?.total ||
                          0) && (
                        <Button
                          onClick={() => {
                            navigate('/app/my?tab=2');
                          }}
                          type="primary"
                          size="small"
                          style={{
                            backgroundColor: '#6268DB',
                            border: '#6268DB',
                            fontSize: '10px',
                            borderRadius: '4px',
                            width: '62px',
                            height: '24px',
                            padding: '2px',
                            marginLeft: '10px',
                          }}
                        >
                          업그레이드
                        </Button>
                      )}
                  </>
                )}
              </span>
            </div>
            <Progress
              strokeColor="rgb(48, 197, 134)"
              percent={
                userResponse?.data?.quota?.notification?.total === 999
                  ? 0
                  : ((userResponse?.data?.quota?.notification?.used || 0) /
                      (userResponse?.data?.quota?.notification?.total || 1)) *
                    100
              }
              showInfo={false}
              width={300}
            />
            <div
              style={{
                fontSize: '13px',
                color: '#6B7280',
                textAlign: 'end',
                wordBreak: 'keep-all',
              }}
            >
              알림톡 발송 제한은 한달마다 초기화됩니다. <br />
              {userResponse?.data?.quota?.resetDate &&
                `(다음 초기화 일자: ${dayjs(
                  userResponse?.data?.quota?.resetDate,
                ).format('M월 D일')})`}
            </div>
          </div>
        </div>
      )}
      <div>
        <div
          style={{
            width: '100%',
            maxWidth: '460px',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>AI 컨텐츠 분석</span>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {Math.min(
                userResponse?.data?.quota?.aiAnalysis?.used || 0,
                userResponse?.data?.quota?.aiAnalysis?.total || 0,
              )}
              /{userResponse?.data?.quota?.aiAnalysis?.total}개
              {currentPlan !== 'ENTERPRISE' &&
                (userResponse?.data?.quota?.aiAnalysis?.used || 0) >=
                  (userResponse?.data?.quota?.aiAnalysis?.total || 0) && (
                  <Button
                    onClick={() => {
                      navigate('/app/my?tab=2');
                    }}
                    type="primary"
                    size="small"
                    style={{
                      backgroundColor: '#6268DB',
                      border: '#6268DB',
                      fontSize: '10px',
                      borderRadius: '4px',
                      width: '62px',
                      height: '24px',
                      padding: '2px',
                      marginLeft: '10px',
                    }}
                  >
                    업그레이드
                  </Button>
                )}
            </span>
          </div>
          <Progress
            strokeColor="rgb(48, 197, 134)"
            percent={
              ((userResponse?.data?.quota?.aiAnalysis?.used || 0) /
                (userResponse?.data?.quota?.aiAnalysis?.total || 1)) *
              100
            }
            showInfo={false}
            width={300}
          />
        </div>
      </div>

      {/* 무료 체험 배너 또는 이용중 상태 */}
      {userResponse?.data?.trial?.available && (
        <div
          style={{
            marginTop: '16px',
            cursor: 'pointer',
            width: '100%',
            maxWidth: '249px',
          }}
          onClick={() => setShowTrialModal(true)}
        >
          <img
            src="/img/free-trial/short.png"
            alt="무료 체험"
            style={{
              width: '100%',
              height: 'auto',
              aspectRatio: '249 / 88',
              borderRadius: '8px',
              display: 'block',
            }}
          />
        </div>
      )}

      {userResponse?.data?.trial?.status === 'ACTIVE' && (
        <div
          style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: '#f8f9ff',
            borderRadius: '8px',
            border: '1px solid #e6e8ff',
            width: '100%',
            maxWidth: '249px',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              color: '#4403B5',
              fontWeight: '600',
              textAlign: 'center',
              lineHeight: '1.4',
            }}
          >
            엔터프라이즈 무료 한달체험 이용중
            {userResponse?.data?.trial?.startedAt &&
              userResponse?.data?.trial?.endsAt && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666',
                    fontWeight: '400',
                    marginTop: '4px',
                  }}
                >
                  {dayjs(userResponse.data.trial.startedAt).format(
                    'YYYY-MM-DD',
                  )}{' '}
                  ~ {dayjs(userResponse.data.trial.endsAt).format('YYYY-MM-DD')}
                </div>
              )}
          </div>
        </div>
      )}

      {/* 무료 체험 모달 */}
      <FreeTrialModal
        open={showTrialModal}
        onCancel={() => setShowTrialModal(false)}
        onOk={() => setShowTrialModal(false)}
      />

      {/* Trial 만료 안내 모달 */}
      <Modal
        title={null}
        open={showExpirationModal}
        onOk={() => {
          setShowExpirationModal(false);
          navigate('/app/my?tab=2'); // 구독 관리 페이지로 이동
        }}
        onCancel={() => setShowExpirationModal(false)}
        okText="확인"
        cancelText="취소"
        okButtonProps={{
          style: {
            backgroundColor: '#4403B5',
            borderColor: '#4403B5',
          },
        }}
        width={416}
        centered
        closable={false}
      >
        <div
          style={{
            paddingTop: '8px',
            paddingBottom: '16px',
            fontSize: '16px',
            color: 'rgba(0, 0, 0, 0.85)',
            textAlign: 'center',
            lineHeight: '1.5',
          }}
        >
          고객님의 서비스가 {daysRemaining}일 뒤 종료될 예정입니다.
          <br />
          연장을 원하시면 유료 플랜으로 전환해 주세요.
        </div>
      </Modal>
    </div>
  );
};

export default SettingQuotaPane;
